import { useState, useEffect, useRef } from "react";
import { api } from "../api/client";
import { I } from "./Icons";
import { clsx } from "./Primitives";

interface SearchResult {
  id: string;
  name: string;
  type: string;
  sub: string;
}

interface Props {
  open: boolean;
  close: () => void;
  go: (screen: string, id?: string) => void;
  openAI: (prefill?: string) => void;
  openMinutes: (prefill?: { dealId?: string } | null) => void;
}

export default function CommandK({ open, close, go, openAI, openMinutes }: Props) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setSel(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const timer = setTimeout(() => {
      api.get<SearchResult[]>(`/api/search?q=${encodeURIComponent(q)}`)
        .then(setResults)
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  const actions: SearchResult[] = [
    { id: "_ask", name: "Ask Saga", type: "action", sub: "⌘J" },
    { id: "_min", name: "New minutes", type: "action", sub: "⌘M" },
  ];

  const all = q.trim() ? results : actions;

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel(s => Math.min(s + 1, all.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
    if (e.key === "Escape") { close(); }
    if (e.key === "Enter" && all[sel]) {
      e.preventDefault();
      pick(all[sel]);
    }
  };

  const pick = (item: SearchResult) => {
    close();
    if (item.id === "_ask") { openAI(""); return; }
    if (item.id === "_min") { openMinutes(null); return; }
    if (item.type === "deal") go("project", item.id);
    else if (item.type === "contact") go("clients", item.id);
    else if (item.type === "email") go("inbox");
  };

  const kindLabel: Record<string, string> = { deal: "DEAL", contact: "CONTACT", email: "EMAIL", action: "ACTION" };

  if (!open) return null;

  return (
    <div className="cmd-overlay" onMouseDown={close}>
      <div className="cmd-panel" onMouseDown={e => e.stopPropagation()}>
        <div className="cmd-input-row">
          {I.search({ size: 18, className: "cmd-search-ic" })}
          <input ref={inputRef} className="cmd-input" placeholder="Search deals, contacts, emails…"
            value={q} onChange={e => { setQ(e.target.value); setSel(0); }} onKeyDown={handleKey} />
          <span className="cmd-esc">esc</span>
        </div>

        <div className="cmd-list">
          {!q.trim() && <div className="cmd-group">Quick actions</div>}
          {q.trim() && results.length === 0 && <div className="cmd-empty">No results for "{q}"</div>}
          {all.map((item, i) => (
            <div key={item.id + item.type} className={clsx("cmd-item", i === sel && "sel")}
              onMouseEnter={() => setSel(i)} onClick={() => pick(item)}>
              <div className="cmd-ic">
                {item.type === "deal" ? I.pipeline({ size: 14 }) :
                 item.type === "contact" ? I.meeting({ size: 14 }) :
                 item.type === "email" ? I.mail({ size: 14 }) :
                 item.id === "_ask" ? I.ai({ size: 14 }) :
                 I.note({ size: 14 })}
              </div>
              <span className="cmd-title">{item.name}</span>
              <span className="cmd-kind">{kindLabel[item.type] || item.type}</span>
              {item.sub && <span className="cmd-sub">{item.sub}</span>}
              {i === sel && <span className="cmd-enter">↵</span>}
            </div>
          ))}
        </div>

        <div className="cmd-foot">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
          <span className="cmd-foot-brand">Saga</span>
        </div>
      </div>
    </div>
  );
}
